import { gql } from '@apollo/client';
import type { FleetAiRecommendationV1, RecommendationRefInput, SubmitRecommendationFeedbackInput, UserFeedbackResponse } from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';

export type { FleetAiRecommendationV1, RecommendationRefInput, SubmitRecommendationFeedbackInput, UserFeedbackResponse };

const RECOMMENDATION_FIELDS = gql`
  fragment RecommendationFields on FleetAiRecommendationV1 {
    fleetId
    journeyId
    journeyName
    recommendationType
    deprecated
    description
    shortDescription
    reasons
    aiPrompt
    criticality
    finishedAt
    doneByUser
    dismissedAt
    createdAt
    updatedAt
  }
`;

export class GraphQLRecommendationsRequester {
  public generateBasicValueRecommendations = async (fleetId: string): Promise<FleetAiRecommendationV1[]> => {
    const res = await graphQLRequester.mutate<{ generateBasicValueRecommendations: FleetAiRecommendationV1[] }>({
      mutation: gql`
          ${RECOMMENDATION_FIELDS}
          mutation generateBasicValueRecommendations($fleetId: ID!) {
            generateBasicValueRecommendations(fleetId: $fleetId) {
              ...RecommendationFields
            }
          }
        `,
      variables: { fleetId },
    });
    return res.generateBasicValueRecommendations;
  };

  public getFleetAiRecommendations = async (fleetId: string): Promise<FleetAiRecommendationV1[]> => {
    const res = await graphQLRequester.query<{ fleetAiRecommendations: FleetAiRecommendationV1[] }>({
      query: gql`
          ${RECOMMENDATION_FIELDS}
          query fleetAiRecommendations($fleetId: ID!) {
            fleetAiRecommendations(fleetId: $fleetId) {
              ...RecommendationFields
            }
          }
        `,
      variables: { fleetId },
    });
    return res.fleetAiRecommendations;
  };

  public completeFleetAiRecommendation = async (fleetId: string, journeyId: string, recommendationType: string): Promise<FleetAiRecommendationV1> => {
    return graphQLRequester
      .mutate<{ completeFleetAiRecommendation: FleetAiRecommendationV1 }>({
        mutation: gql`
          ${RECOMMENDATION_FIELDS}
          mutation completeFleetAiRecommendation($fleetId: ID!, $journeyId: String!, $recommendationType: RecommendationType!) {
            completeFleetAiRecommendation(fleetId: $fleetId, journeyId: $journeyId, recommendationType: $recommendationType) {
              ...RecommendationFields
            }
          }
        `,
        variables: { fleetId, journeyId, recommendationType },
      })
      .then((res) => res.completeFleetAiRecommendation);
  };

  public dismissFleetAiRecommendation = async (fleetId: string, journeyId: string, recommendationType: string): Promise<FleetAiRecommendationV1> => {
    return graphQLRequester
      .mutate<{ dismissFleetAiRecommendation: FleetAiRecommendationV1 }>({
        mutation: gql`
          ${RECOMMENDATION_FIELDS}
          mutation dismissFleetAiRecommendation($fleetId: ID!, $journeyId: String!, $recommendationType: RecommendationType!) {
            dismissFleetAiRecommendation(fleetId: $fleetId, journeyId: $journeyId, recommendationType: $recommendationType) {
              ...RecommendationFields
            }
          }
        `,
        variables: { fleetId, journeyId, recommendationType },
      })
      .then((res) => res.dismissFleetAiRecommendation);
  };

  public submitRecommendationFeedback = async (input: SubmitRecommendationFeedbackInput): Promise<UserFeedbackResponse> => {
    return graphQLRequester
      .mutate<{ submitRecommendationFeedback: UserFeedbackResponse }>({
        mutation: gql`
          mutation submitRecommendationFeedback($input: SubmitRecommendationFeedbackInput!) {
            submitRecommendationFeedback(input: $input) {
              id
              fleetId
              userId
              feedback
              doNotRecommendAgain
            }
          }
        `,
        variables: { input },
      })
      .then((res) => res.submitRecommendationFeedback);
  };
}
